<template>
  <div class="tags-view-container">
    <div class="tags-view-wrapper" ref="scrollWrap">
      <router-link
        v-for="tag in visitedViews"
        ref="tag"
        :key="tag.path"
        :class="['tags-view-item', isActive(tag) ? 'active' : '']"
        :to="{ path: tag.path, query: toQuery(tag) }"
        @contextmenu.prevent.native="openMenu(tag, $event)"
      >
        {{ tagDisplayTitle(tag) }}
        <span
          v-if="!tag.affix"
          class="el-icon-close"
          @click.prevent.stop="closeSelectedTag(tag)"
        />
      </router-link>
    </div>
    <ul v-show="visible" :style="{ left: left + 'px', top: top + 'px' }" class="contextmenu">
      <li @click="refreshSelectedTag(selectedTag)">
        <i class="el-icon-refresh" /> {{ $t("tags.refresh") }}
      </li>
      <li :class="{ disabled: selectedTag.affix }" @click="closeSelectedTag(selectedTag)">
        <i class="el-icon-close" /> {{ $t("tags.close") }}
      </li>
      <li :class="{ disabled: !canCloseOthers }" @click="closeOthersTags">
        <i class="el-icon-circle-close" /> {{ $t("tags.closeOthers") }}
      </li>
      <li :class="{ disabled: !canCloseLeft }" @click="closeLeftTags">
        <i class="el-icon-back" /> {{ $t("tags.closeLeft") }}
      </li>
      <li @click="closeAllTags">
        <i class="el-icon-minus" /> {{ $t("tags.closeAll") }}
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState } from "vuex";
import routes from "@/router/routes";
import { deepFlattenRoutes } from "@/utils/menu";

export default {
  data() {
    return {
      visible: false,
      top: 0,
      left: 0,
      selectedTag: {},
    };
  },
  computed: {
    ...mapState(["visitedViews"]),
    canCloseOthers() {
      return this.visitedViews.filter((v) => !v.affix).length > 1;
    },
    canCloseLeft() {
      const idx = this.visitedViews.findIndex((v) => v.path === this.selectedTag.path);
      if (idx <= 0) return false;
      return this.visitedViews.slice(0, idx).some((v) => !v.affix);
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.addView();
      },
    },
    visible(val) {
      if (val) {
        document.body.addEventListener("click", this.closeMenu);
      } else {
        document.body.removeEventListener("click", this.closeMenu);
      }
    },
  },
  methods: {
    toQuery(tag) {
      if (!tag.fullPath) return {};
      const i = tag.fullPath.indexOf("?");
      if (i < 0) return {};
      const search = tag.fullPath.slice(i + 1);
      return search.split("&").reduce((acc, pair) => {
        const [k, v] = pair.split("=");
        if (k) acc[decodeURIComponent(k)] = decodeURIComponent(v || "");
        return acc;
      }, {});
    },
    isActive(tag) {
      return tag.path === this.$route.path;
    },
    tagDisplayTitle(tag) {
      return this.$menuLabel(tag.path, tag.title);
    },
    getTitle(route) {
      const found = deepFlattenRoutes(routes).find((i) => i.path === route.path);
      const raw = (found && found.title) || route.meta.title || "";
      return this.$menuLabel(route.path, raw);
    },
    addView() {
      const route = this.$route;
      if (!route.path || route.path === "/" || route.meta.hideMenu || route.meta.hiddenTag) {
        return;
      }
      if (route.path === "/login" || route.path === "/register" || route.path === "/redirect") {
        return;
      }
      if (!route.matched || !route.matched.length) {
        return;
      }
      const title = this.getTitle(route);
      if (!title) {
        return;
      }
      this.$store.commit("addVisitedView", {
        path: route.path,
        fullPath: route.fullPath,
        title,
        affix: route.path === "/homePage",
      });
    },
    openMenu(tag, e) {
      const menuMinWidth = 120;
      const offsetLeft = this.$el.getBoundingClientRect().left;
      const offsetWidth = this.$el.offsetWidth;
      const maxLeft = offsetWidth - menuMinWidth;
      let left = e.clientX - offsetLeft + 6;
      if (left > maxLeft) left = maxLeft;
      this.left = left;
      this.top = e.clientY - this.$el.getBoundingClientRect().top + 8;
      this.visible = true;
      this.selectedTag = tag;
    },
    closeMenu() {
      this.visible = false;
    },
    toLastView(views, excludedPath) {
      const latest = [...views].reverse().find((v) => v.path !== excludedPath) || views[0];
      if (latest) {
        this.$router.push(latest.fullPath || latest.path);
      } else {
        this.$router.push("/homePage");
      }
    },
    refreshSelectedTag(view) {
      if (!view || !view.path) return;
      const target = view.fullPath || view.path;
      this.$router.replace({
        path: "/redirect",
        query: { path: target },
      });
    },
    closeSelectedTag(view) {
      if (!view || view.affix) return;
      this.$store.commit("delVisitedView", view.path);
      if (this.isActive(view)) {
        this.toLastView(this.visitedViews, view.path);
      }
    },
    closeOthersTags() {
      if (!this.canCloseOthers) return;
      const view = this.selectedTag;
      this.$store.commit("delOthersVisitedViews", view.path);
      if (this.$route.path !== view.path) {
        this.$router.push(view.fullPath || view.path);
      }
    },
    closeLeftTags() {
      if (!this.canCloseLeft) return;
      const view = this.selectedTag;
      this.$store.commit("delLeftVisitedViews", view.path);
      const still = this.visitedViews.find((v) => v.path === this.$route.path);
      if (!still) {
        this.$router.push(view.fullPath || view.path);
      }
    },
    closeAllTags() {
      this.$store.commit("delAllVisitedViews");
      const first = this.visitedViews[0];
      if (first) {
        this.$router.push(first.fullPath || first.path);
      } else {
        this.$router.push("/homePage");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tags-view-container {
  position: relative;
  height: 40px;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  .tags-view-wrapper {
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 0 8px;
    display: flex;
    align-items: center;
  }
  .tags-view-item {
    display: inline-flex;
    align-items: center;
    height: 26px;
    line-height: 26px;
    border: none;
    border-radius: 4px;
    color: #606266;
    background: #f0f2f5;
    padding: 0 12px;
    font-size: 12px;
    margin-right: 8px;
    text-decoration: none;
    flex-shrink: 0;
    transition: background-color 0.15s ease, color 0.15s ease;
    .el-icon-close {
      width: 16px;
      height: 16px;
      margin-left: 6px;
      border-radius: 50%;
      text-align: center;
      line-height: 16px;
      font-size: 12px;
      &:hover {
        background-color: rgba(0, 0, 0, 0.12);
        color: #fff;
      }
    }
    &.active {
      background-color: #409eff;
      color: #fff;
      .el-icon-close {
        color: #fff;
        &:hover {
          background: rgba(255, 255, 255, 0.28);
        }
      }
    }
  }
  .contextmenu {
    margin: 0;
    background: #fff;
    z-index: 3000;
    position: absolute;
    list-style-type: none;
    padding: 5px 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    color: #333;
    box-shadow: 2px 2px 8px 0 rgba(0, 0, 0, 0.18);
    li {
      margin: 0;
      padding: 8px 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      i {
        margin-right: 8px;
        font-size: 14px;
      }
      &:hover {
        background: #eee;
      }
      &.disabled {
        color: #c0c4cc;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }
}
</style>
